import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useMutation, useQuery } from '@apollo/client';
// API
import { SWITCH_TO_CREATOR } from 'api/auth/mutations';
import { CREATE_PRESIGNED_AVATAR_URL } from 'api/upload/mutations';
import { CONNECTED_SOCIAL_ACCOUNTS } from 'api/auth/queries';
// Types
import { ConnectedSocialAccounts } from 'api/auth/types/ConnectedSocialAccounts';
import {
  SwitchToCreator,
  SwitchToCreatorVariables,
} from 'api/auth/types/SwitchToCreator';
import {
  CreatePresignedAvatarUrl,
  CreatePresignedAvatarUrlVariables,
} from 'api/upload/types/CreatePresignedAvatarUrl';
// Context
import { OnboardingFormData, OTPValidation } from 'context/OnboardingContext';
// Helpers
import { uploadImagesToS3 } from 'helpers/single-uploader';
import { getEnvLink } from 'helpers/routes';
import {
  addOtpFields,
  persistFormFields,
  shouldRevalidateOtp,
} from 'helpers/profile-setup';
import { getExtTalentBrandDealAttributionFromStorage } from 'helpers/extTalentBrandDeal';
import { getMatchupInviteTokenFromLocalStorage } from 'helpers/storage';
import {
  finishCreatorSignUpFlow,
  redirectToAppStore,
} from 'helpers/creatorSignUp';
// Validation
import {
  onboardingValidationSchema,
  OnboardingValidationContext,
} from './onboardingValidationSchema';
// Hooks
import { useGetCurrUser, useOnboardingContext } from 'hooks';
import { useSocialIntegrationListener } from 'hooks/useSocialIntegrationListener';
// Components
import FullScreenStepperModal, {
  FullScreenStepperModalFooter,
} from 'components/common3/FullScreenStepperModal/FullScreenStepperModal';
import ProfileStep from '../ProfileStep/ProfileStep';
import BasicInfoStep from '../BasicInfoStep/BasicInfoStep';
import SocialMediaStep from '../SocialMediaStep/SocialMediaStep';
import { showToast } from 'components/common/Toast/Toast';
import SelectFacebookPageModal from 'components/SocialIntegration/SelectFacebookPageModal/SelectFacebookPageModal';
import SocialIntegrationErrorModal from 'components/SocialIntegration/SocialIntegrationErrorModal/SocialIntegrationErrorModal';
import { GetFacebookPages } from 'api/social-media-connections/types/GetFacebookPages';
import { SocialIntegrationErrorDialogData } from 'helpers/social-integration-errors';

const Onboarding = () => {
  const { data, loading: userDataLoading } = useGetCurrUser();
  const {
    data: connectedSocialAccountsData,
  } = useQuery<ConnectedSocialAccounts>(CONNECTED_SOCIAL_ACCOUNTS);
  const [
    fbCallbackDialogData,
    setFbCallbackDialogData,
  ] = useState<GetFacebookPages | null>(null);
  const [
    socialIntegrationError,
    setSocialIntegrationError,
  ] = useState<SocialIntegrationErrorDialogData | null>(null);

  const {
    otpValidation,
    setShowCreatorSetupModal,
    setShowOTPModal,
    setOTPValidation,
    isAddSocialAccountModalOpen,
  } = useOnboardingContext();

  const [switchToCreator, { loading: loadingSwitchToCreator }] = useMutation<
    SwitchToCreator,
    SwitchToCreatorVariables
  >(SWITCH_TO_CREATOR, {
    onCompleted: () => {
      setOTPValidation(null);
      const isExtTalentFlow = Boolean(
        getExtTalentBrandDealAttributionFromStorage()
      );
      // MatchupInviteWatcher sends fight-card invitees to the stream page instead.
      const hasMatchupInvite = Boolean(getMatchupInviteTokenFromLocalStorage());
      const redirectToStore = finishCreatorSignUpFlow(isExtTalentFlow);

      if (!isExtTalentFlow && !hasMatchupInvite && !redirectToStore) {
        setShowCreatorSetupModal(true);
      }
      handleModalClose();

      if (redirectToStore) redirectToAppStore();
    },
  });

  const [
    createPresignedAvatarUrl,
    { loading: loadingCreatePresignedUrl },
  ] = useMutation<CreatePresignedAvatarUrl, CreatePresignedAvatarUrlVariables>(
    CREATE_PRESIGNED_AVATAR_URL
  );

  const loading = loadingSwitchToCreator || loadingCreatePresignedUrl;

  const {
    onboardingStep,
    showOnboardingModal,
    onboardingData,
    setShowOnboardingModal,
    setOnboardingStep,
    resetOnboardingStateAfterSwitch,
    setOnboardingData,
  } = useOnboardingContext();
  const formValidationContextRef = useRef<OnboardingValidationContext>({
    linkedSocialAccountsCount: 0,
    isAddSocialAccountModalOpen: false,
  });
  formValidationContextRef.current.linkedSocialAccountsCount =
    connectedSocialAccountsData?.connectedSocialAccounts?.length || 0;
  formValidationContextRef.current.isAddSocialAccountModalOpen = isAddSocialAccountModalOpen;

  const methods = useForm<OnboardingFormData>({
    resolver: yupResolver(onboardingValidationSchema(onboardingStep)),
    defaultValues: {
      username: '',
      profileName: '',
      avatar: '',
      accountType: undefined,
      experienceLevel: undefined,
      sport: undefined,
      birthDate: null,
      gender: undefined,
      bio: '',
      phoneNumber: '',
      phoneOptIn: false,
      location: undefined,
      socialAccounts: [
        {
          platform: undefined,
          link: '',
          isAdded: false,
        },
      ],
      email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    context: formValidationContextRef.current,
  });

  useEffect(() => {
    if (!data?.me || onboardingStep !== 1) {
      return;
    }

    if (data?.me) {
      methods.reset({
        username: onboardingData?.username || data?.me?.slug || '',
        profileName:
          onboardingData?.profileName ||
          `${data?.me?.firstName || ''} ${data?.me?.lastName || ''}`.trim(),
        avatar: onboardingData?.avatar || data?.me?.avatarUrl || '',
        phoneNumber: data?.me?.phoneNumber || '',
        phoneOptIn: data?.me?.phoneOptIn || false,
        email: data?.me?.email || '',
        location: onboardingData?.location,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const redirectUrl = `${getEnvLink()}/dashboard`;
  const { getValues, reset } = methods;
  const {
    restorePendingIntegration,
    pendingProvider,
  } = useSocialIntegrationListener();
  useEffect(() => {
    restorePendingIntegration<{
      values: OnboardingFormData;
      step: number;
      otpValidation?: OTPValidation | null;
      files?: Record<string, File>;
    }>(
      (state) => {
        if (state.otpValidation) {
          setOTPValidation(state.otpValidation);
        }
        const values = state.values?.birthDate
          ? { ...state.values, birthDate: new Date(state.values.birthDate) }
          : state.values;
        setOnboardingData({
          ...values,
          ...(state.files ? { avatar: state.files?.avatar } : {}),
        });
        setOnboardingStep(state.step || 3);
        setShowOnboardingModal(true);
        reset({
          ...getValues(),
          ...values,
          ...(state.files ? { avatar: state.files?.avatar } : {}),
        });
      },
      redirectUrl,
      {
        onAuthorized: ({ facebookPages, errorDialog }) => {
          if (facebookPages) setFbCallbackDialogData(facebookPages);
          if (errorDialog) setSocialIntegrationError(errorDialog);
        },
      }
    );
  }, [
    getValues,
    reset,
    redirectUrl,
    setOnboardingData,
    setOnboardingStep,
    setShowOnboardingModal,
    setOTPValidation,
    restorePendingIntegration,
  ]);

  const handleModalClose = useCallback(() => {
    setShowOnboardingModal(false);
    resetOnboardingStateAfterSwitch();
    methods.reset({});
  }, [setShowOnboardingModal, resetOnboardingStateAfterSwitch, methods]);

  useEffect(() => {
    if (!userDataLoading && showOnboardingModal && !data?.me) {
      handleModalClose();
    }
  }, [data, userDataLoading, showOnboardingModal, handleModalClose]);

  const handlePrevStepClick = useCallback(() => {
    if (onboardingStep > 1) {
      const fieldsToPersist = [
        'username',
        'avatar',
      ] as (keyof OnboardingFormData)[];
      const updatedFields = persistFormFields<OnboardingFormData>(
        methods,
        onboardingData,
        fieldsToPersist
      );
      setOnboardingData(updatedFields);
      setOnboardingStep(onboardingStep - 1);
    }
    // onboardingData isn't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingStep, setOnboardingStep]);

  const handleSubmit = methods.handleSubmit(
    async (values) => {
      try {
        // Check if we need to show OTP modal for phone number
        if (onboardingStep === 2 && values.email && values.phoneNumber) {
          const {
            phoneNeedsVerification,
            emailNeedsVerification,
          } = shouldRevalidateOtp(
            values.email,
            values.phoneNumber,
            data?.me,
            true
          );

          if (
            emailNeedsVerification &&
            phoneNeedsVerification &&
            !otpValidation
          ) {
            setShowOTPModal(true);
            return;
          }
        }

        if (onboardingStep === 1) {
          setOnboardingData(values);
          setOnboardingStep(2);
        }

        if (onboardingStep === 2) {
          setOnboardingData(values);
          setOnboardingStep(3);
        }

        if (onboardingStep === 3) {
          if (
            onboardingData &&
            onboardingData.phoneNumber &&
            onboardingData.accountType &&
            onboardingData.username &&
            onboardingData.profileName
          ) {
            let avatarKey = '';
            if (
              onboardingData.avatar &&
              typeof onboardingData.avatar !== 'string'
            ) {
              const filenameParts = onboardingData.avatar.name.split('.');

              const {
                data: presignedUrlsData,
              } = await createPresignedAvatarUrl({
                variables: {
                  input: {
                    ext: filenameParts[filenameParts.length - 1],
                    contentType: onboardingData.avatar.type,
                  },
                },
              });

              if (presignedUrlsData?.createPresignedAvatarUrl) {
                await uploadImagesToS3([
                  {
                    ...presignedUrlsData.createPresignedAvatarUrl,
                    file: onboardingData.avatar,
                  },
                ]);

                avatarKey = presignedUrlsData.createPresignedAvatarUrl.key;
              }
            }

            const addedSocialAccounts =
              values.socialAccounts?.filter(
                (socialAccount) => socialAccount.isAdded
              ) || [];

            await switchToCreator({
              variables: {
                input: {
                  username: onboardingData.username,
                  profileName: onboardingData.profileName,
                  ...(avatarKey && { avatarKey }),
                  ...(onboardingData.bio && { bio: onboardingData.bio }),
                  ...(onboardingData.location && {
                    country: onboardingData.location.countryCode,
                    city: onboardingData.location.city,
                    ...(onboardingData.location.state && {
                      state: onboardingData.location.stateCode,
                    }),
                    location: onboardingData.location.coordinates,
                  }),
                  ...(onboardingData.experienceLevel && {
                    level: onboardingData.experienceLevel.value,
                  }),
                  ...(onboardingData.birthDate && {
                    birthDate: dayjs(onboardingData.birthDate).format(
                      'YYYY-MM-DD'
                    ),
                  }),
                  ...(onboardingData.gender && {
                    gender: onboardingData.gender.value,
                  }),
                  sportIds: onboardingData.sport
                    ? [onboardingData.sport.id]
                    : [],
                  subtype: onboardingData.accountType.value,
                  phoneNumberE164: onboardingData.phoneNumber as string,
                  // TODO: check with PROD if this is okay not be shown in form
                  phoneOptIn: true,
                  ...(addedSocialAccounts.length > 0 && {
                    socialAccounts: addedSocialAccounts.map(
                      (socialAccount) => ({
                        platform: socialAccount.platform,
                        link: socialAccount.link,
                      })
                    ),
                  }),
                  ...addOtpFields(otpValidation),
                },
              },
            });

            handleModalClose();
          }
        }
      } catch (error) {
        setOTPValidation(null);
        console.error(error);
        showToast({
          type: 'error',
          message: error?.message || 'Something went wrong. Please try again.',
        });
      }
    },
    (err) => console.log(err)
  );

  const getModalFooter = (): FullScreenStepperModalFooter | undefined => {
    if (onboardingStep === 1 || onboardingStep === 2 || onboardingStep === 3) {
      return {
        totalStepsNumber: 4,
        stepNumber: onboardingStep,
        submitButtonProps: {
          onClick: handleSubmit,
          variant: 'primary',
          loading: loading || Boolean(pendingProvider),
        },
        submitButtonText: 'Next',
        showPrevButton: true,
        prevButtonText: 'Back',
        prevButtonProps: {
          onClick: onboardingStep > 1 ? handlePrevStepClick : handleModalClose,
          variant: 'secondary',
        },
      };
    }

    return;
  };

  return (
    <FullScreenStepperModal
      open={showOnboardingModal}
      onClose={handleModalClose}
      title="JOIN AS A CREATOR"
      footer={getModalFooter()}
    >
      <FormProvider {...methods}>
        <SelectFacebookPageModal
          isOpen={!!fbCallbackDialogData}
          onClose={() => setFbCallbackDialogData(null)}
          pagesData={fbCallbackDialogData?.getFacebookPages}
          onError={setSocialIntegrationError}
        />
        <SocialIntegrationErrorModal
          isOpen={!!socialIntegrationError}
          onClose={() => setSocialIntegrationError(null)}
          error={socialIntegrationError}
        />

        {onboardingStep === 1 && <ProfileStep />}

        {onboardingStep === 2 && <BasicInfoStep />}

        {onboardingStep === 3 && (
          <SocialMediaStep isNewSocialMediaIntegration />
        )}
      </FormProvider>
    </FullScreenStepperModal>
  );
};

export default Onboarding;
